import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared in-app Custom Quote Request modal.
 *
 * Replaces the legacy `mailto:` button — collects the same fields the
 * EG3 team used to ask for in the back-and-forth thread, lets customers
 * drop design files, and submits everything to the backend which emails
 * `jfishburn@eg3.ca` in the background.
 *
 * @param {boolean} open — controlled open state
 * @param {() => void} onClose — closes the modal without submitting
 * @param {() => void} onSubmitted — fires AFTER a successful submit
 * @param {object} [prefill] — { category, productType, product } strings
 *                             that prefill the Product field so customers
 *                             don't re-type what they already picked in
 *                             the quote flow. Omit for blank splash-page
 *                             launches.
 */
export const CustomQuoteModal = ({ open, onClose, onSubmitted, prefill }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [location, setLocation] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [supportCall, setSupportCall] = useState(false);
  const [product, setProduct] = useState(
    prefill?.product
      || [prefill?.category, prefill?.productType].filter(Boolean).join(' — ')
      || ''
  );
  const [totalQuantity, setTotalQuantity] = useState('');
  const [applicationUse, setApplicationUse] = useState('');
  const [widthIn, setWidthIn] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [useType, setUseType] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = useCallback(() => {
    setFirstName(''); setLastName(''); setLocation('');
    setEmail(''); setPhone(''); setSupportCall(false);
    setTotalQuantity(''); setApplicationUse('');
    setWidthIn(''); setHeightIn(''); setUseType('');
    setAdditionalInstructions(''); setFiles([]); setErr(null);
  }, []);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const validate = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return 'Please enter a valid email.';
    if (!phone.trim()) return 'Phone number is required.';
    if (!product.trim()) return 'Please tell us which product.';
    if (!totalQuantity.trim()) return 'Total quantity is required.';
    if (!widthIn.trim()) return 'Width (in inches) is required.';
    if (!useType.trim()) return 'Please choose personal or business use.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) { setErr(msg); return; }
    setErr(null);
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('first_name', firstName);
      form.append('last_name', lastName);
      form.append('email', email.trim());
      form.append('phone', phone);
      form.append('location', location);
      form.append('support_call', supportCall ? 'true' : 'false');
      if (prefill?.category) form.append('category', prefill.category);
      if (prefill?.productType) form.append('product_type', prefill.productType);
      form.append('product', product);
      form.append('total_quantity', totalQuantity);
      form.append('application_use', applicationUse);
      form.append('width_in', widthIn);
      form.append('height_in', heightIn);
      form.append('use_type', useType);
      form.append('additional_instructions', additionalInstructions);
      (files || []).forEach((f) => form.append('files', f));
      await axios.post(`${API_URL}/api/quote-request/submit`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45000,
      });
      resetForm();
      onSubmitted?.();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message;
      log.error('Custom quote submit failed:', error);
      setErr(detail || 'Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white"
        data-testid="custom-quote-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2F5D3A]">
            Request a Custom Quote
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            Fill in as much as you can — our EG3 Product Experts will reply with a personalized quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4" data-testid="custom-quote-form">
          {/* Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cq-first">First name</Label>
              <Input id="cq-first" data-testid="cq-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cq-last">Last name</Label>
              <Input id="cq-last" data-testid="cq-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="cq-location">Location</Label>
            <select
              id="cq-location"
              data-testid="cq-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-[#4A7C59]"
            >
              <option value="">Please Select</option>
              <option value="Canada">Canada</option>
              <option value="United States">United States</option>
              <option value="Mexico">Mexico</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="cq-email">Email <span className="text-red-500">*</span></Label>
            <Input id="cq-email" type="email" required data-testid="cq-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {/* Phone + support call */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label htmlFor="cq-phone">Phone number <span className="text-red-500">*</span></Label>
              <Input id="cq-phone" required data-testid="cq-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 pb-2">
              <Checkbox
                checked={supportCall}
                onCheckedChange={(v) => setSupportCall(!!v)}
                data-testid="cq-support-call"
              />
              <span className="text-sm text-gray-700">Request a support call</span>
            </label>
          </div>

          {/* Product */}
          <div>
            <Label htmlFor="cq-product">Product <span className="text-red-500">*</span></Label>
            <Input
              id="cq-product"
              required
              data-testid="cq-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={prefill ? '' : 'e.g., Holographic Die-Cut Stickers'}
            />
          </div>

          {/* Quantity + application */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cq-qty">Total Quantity <span className="text-red-500">*</span></Label>
              <Input id="cq-qty" required data-testid="cq-quantity" type="number" min="1" value={totalQuantity} onChange={(e) => setTotalQuantity(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cq-app">Application Use</Label>
              <Input id="cq-app" data-testid="cq-application" value={applicationUse} onChange={(e) => setApplicationUse(e.target.value)} placeholder="e.g., product labels, giveaways" />
            </div>
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cq-w">Width in Inches <span className="text-red-500">*</span></Label>
              <Input id="cq-w" required data-testid="cq-width" type="number" step="0.01" min="0.01" value={widthIn} onChange={(e) => setWidthIn(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cq-h">Height in Inches</Label>
              <Input id="cq-h" data-testid="cq-height" type="number" step="0.01" min="0.01" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} />
            </div>
          </div>

          {/* Use type */}
          <div>
            <Label htmlFor="cq-use">Personal or business use? <span className="text-red-500">*</span></Label>
            <select
              id="cq-use"
              required
              data-testid="cq-use-type"
              value={useType}
              onChange={(e) => setUseType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-[#4A7C59]"
            >
              <option value="">Please Select</option>
              <option value="Personal">Personal</option>
              <option value="Business">Business</option>
            </select>
          </div>

          {/* Instructions */}
          <div>
            <Label htmlFor="cq-notes">Additional Instructions</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Leave any further instructions necessary for our product experts including desired end-use. Make sure to check your spam/junk folder for our reply.
            </p>
            <Textarea
              id="cq-notes"
              data-testid="cq-notes"
              rows={4}
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              maxLength={2000}
            />
          </div>

          {/* Upload */}
          <div>
            <Label>Upload your design (multiple files can be selected to be uploaded)</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">Vector file preferred (.ai, .svg, .eps, .pdf). Max 25 MB/file, 75 MB total.</p>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-[#4A7C59]/40 rounded-md p-4 cursor-pointer hover:border-[#4A7C59] hover:bg-green-50/40 transition-colors"
              data-testid="cq-upload-dropzone"
            >
              <div className="flex items-center justify-center gap-2 text-[#4A7C59] text-sm font-medium">
                <Upload className="w-4 h-4" />
                {files.length ? `${files.length} file${files.length === 1 ? '' : 's'} ready` : 'Choose files'}
              </div>
              {files.length > 0 && (
                <ul className="mt-2 text-xs text-gray-600 space-y-0.5 max-h-24 overflow-y-auto">
                  {files.map((f) => (
                    // File-name + size is a stable tuple — collisions are
                    // benign (re-render the same row) and avoid the
                    // index-as-key pitfall on remove/reorder.
                    <li key={`${f.name}-${f.size}`} className="truncate">• {f.name} ({(f.size / 1024).toFixed(0)} KB)</li>
                  ))}
                </ul>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.ai,.eps,.svg,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,.psd"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="hidden"
              data-testid="cq-file-input"
            />
          </div>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="cq-error">
              {err}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-gray-300"
              onClick={handleClose}
              disabled={submitting}
              data-testid="cq-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white font-semibold"
              data-testid="cq-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send Request'
              )}
            </Button>
          </div>
        </form>
        {/* iter114s.61 — Removed the legacy custom close button; the
            shadcn `<DialogContent>` already renders its own X in the
            top-right corner. Keeping both produced two overlapping
            close icons. */}
      </DialogContent>
    </Dialog>
  );
};

export default CustomQuoteModal;
